import {
  CRUDInputElementConstants,
  ECRUDFormLayoutType,
  EFormFieldSource,
  EFormSource,
  EProjectType,
  ICRUDFormDiMetadata,
  ICRUDFormField,
  IDatabaseColumnModel,
  IDatabaseTableModel,
  IFormSection,
  IWFParameter,
  IWorkFlow
} from "@kuika/kuika-cl-model";
import _ from "lodash";
import React from "react";
import {
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
  Droppable,
  DroppableProvided
} from "react-beautiful-dnd";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import type { IElementTypeOption } from "../../../shared/utility/form-element-type-options";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import DesktopSvg from "../../../svgs/desktop.svg";
import MobileSvg from "../../../svgs/mobile.svg";
import BooleanSvg from "../../../svgs/parameter-type-icons/boolean.svg";
import DateTimeOffsetSvg from "../../../svgs/parameter-type-icons/date-time-offset.svg";
import DateTimeSvg from "../../../svgs/parameter-type-icons/date-time.svg";
import DateSvg from "../../../svgs/parameter-type-icons/date.svg";
import DecimalSvg from "../../../svgs/parameter-type-icons/decimal.svg";
import DoubleSvg from "../../../svgs/parameter-type-icons/double.svg";
import FileContentSvg from "../../../svgs/parameter-type-icons/file-content.svg";
import GuidSvg from "../../../svgs/parameter-type-icons/guid.svg";
import ImageSvg from "../../../svgs/parameter-type-icons/image.svg";
import Int16Svg from "../../../svgs/parameter-type-icons/int16.svg";
import Int32Svg from "../../../svgs/parameter-type-icons/int32.svg";
import Int64Svg from "../../../svgs/parameter-type-icons/int64.svg";
import LocationSvg from "../../../svgs/parameter-type-icons/location.svg";
import StringSvg from "../../../svgs/parameter-type-icons/string.svg";
import CancelCircleSvg from "../../../svgs/small-icons/cancel-circle.svg";
import DetailMenuSvg from "../../../svgs/small-icons/detail-menu.svg";
import DragHandleSvg from "../../../svgs/small-icons/drag-handle.svg";
import PlusIconSvg from "../../../svgs/small-icons/plus-icon.svg";
import TableSvg from "../../../svgs/table.svg";
import TabletSvg from "../../../svgs/tablet.svg";
import KuikaBadge from "../../ui-kit/kuika-badge/kuika-badge";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaCheckbox from "../../ui-kit/kuika-checkbox/kuika-checkbox";
import KuikaComponentIcon from "../../ui-kit/kuika-component-icon/kuika-component-icon";
import KuikaDropdownMenu from "../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import "./form-section.scss";
import KuikaTooltip from "../../ui-kit/kuika-tooltip/kuika-tooltip";
import { ICustomSourceOptions } from "./form-wizard-custom";
interface IFormSectionProps {
  metadata: ICRUDFormDiMetadata | undefined;
  sectionIndex: number;
  section: IFormSection;
  lastSyncedSectionSnapshot: IFormSection | undefined;
  updateSection: (sectionIndex: number, section: IFormSection) => void;
  onClickAddItem: (sectionIndex: number) => void;
  onClickDeleteItem: (sectionIndex: number, itemIndex: number) => void;
  tables: IDatabaseTableModel[];
  getElementTypeOptions: (typeName: string) => IElementTypeOption[];
  isEditMode: boolean;
  isCreateMode: boolean;
  formSource: EFormSource;
  customSourceOptions: ICustomSourceOptions | null;
  setSectionsHeaderEditingInProgressValue: (isEditingInProgress: boolean) => void;
  setFieldsTouchedValue: (fieldId: string, isTouched: boolean) => void;
  idsOfFieldsWhichFieldNamesAreTouched: string[];
  setFieldsNewColumnActiveValue: (fieldId: string, isNewColumnActive: boolean) => void;
  idsOfFieldsWithNewColumnActive: string[];
}

interface IFormSectionState {
  isRenamingSection: boolean;
  sectionTitle: string | undefined;
}

// KOA-17362 — single definition, shared with the pure widget-option table the DSL
// validates against (`shared/utility/form-element-type-options`). Re-exported here
// because this module was the original home and several imports point at it.
export type { IElementTypeOption };

class FormSection extends React.Component<IFormSectionProps & IWithSDProps, IFormSectionState> {
  constructor(props: IFormSectionProps & IWithSDProps) {
    super(props);
    this.state = {
      isRenamingSection: false,
      sectionTitle: undefined
    };
  }

  componentDidMount() {
    this.setState({
      sectionTitle: this.props.section.title ?? `Section ${this.props.sectionIndex + 1}`
    });

    if (this.getIsAlsoCreateTableEnabled()) {
      this.props.section.items.forEach((item: ICRUDFormField) => {
        this.props.setFieldsNewColumnActiveValue(item.id, true);
      });
    }

    this.applyWorkflowRecommendedElementTypes();
  }

  componentDidUpdate(prevProps: Readonly<IFormSectionProps & IWithSDProps>) {
    if (this.getIsAlsoCreateTableEnabled()) {
      if (_.isEqual(this.props.section.items, prevProps.section.items)) {
        return;
      }

      this.props.section.items.forEach((item: ICRUDFormField) => {
        this.props.setFieldsNewColumnActiveValue(item.id, true);
      });
    }

    if (!_.isEqual(prevProps.section.items, this.props.section.items)) {
      this.applyWorkflowRecommendedElementTypes();
    }
  }

  private getIsAlsoCreateTableEnabled = (): boolean => {
    return this.props.formSource === EFormSource.Custom && !_.isEmpty(this.props.customSourceOptions?.tableName);
  };

  private applyWorkflowRecommendedElementTypes = () => {
    try {
      if (this.props.formSource !== EFormSource.WorkflowBo) return;
      const dict:
        | Record<
            string,
            {
              paramName: string;
              paramElement: string;
              isRequired?: boolean;
              isReadonly?: boolean;
            }[]
          >
        | undefined = (window as any)?.kuikaWorkflowParamElements;
      const processId = this.props.metadata?.linkedWorkflowBo?.processId;
      const stepId = this.props.metadata?.linkedWorkflowBo?.stepId;
      if (!dict || !processId || !stepId) return;

      const process = this.props.sd.workFlowList.find((wf) => wf.id === processId);
      const processAny: any = process;
      const step = processAny?.nodes?.steps?.find((s: any) => s.id === stepId);
      const screenName = step?.name;
      if (!screenName) return;
      const entries = dict[screenName] || [];
      if (!entries.length) return;
      const map = new Map<string, { paramElement: string; isRequired?: boolean; isReadonly?: boolean }>(
        entries.map((e) => [
          e.paramName?.toLowerCase(),
          {
            paramElement: e.paramElement,
            isRequired: e.isRequired,
            isReadonly: e.isReadonly
          }
        ])
      );

      const items: ICRUDFormField[] = _.cloneDeep(this.props.section.items);
      let changed = false;
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.source === EFormFieldSource.WorkflowBusinessObjectParameter && item.linkedWorkflowBoParameterName) {
          const key = item.linkedWorkflowBoParameterName.toLowerCase();
          const elementInfo = map.get(key);
          if (elementInfo) {
            const options = this.props.getElementTypeOptions(item.typeName);
            if (
              options.some((opt) => opt.name === elementInfo.paramElement) &&
              item.elementType !== elementInfo.paramElement
            ) {
              item.elementType = elementInfo.paramElement;
              changed = true;
            }

            if (elementInfo.isRequired !== undefined && item.isRequired !== elementInfo.isRequired) {
              item.isRequired = elementInfo.isRequired;
              changed = true;
            }
            if (elementInfo.isReadonly !== undefined && item.isReadonlyField !== elementInfo.isReadonly) {
              item.isReadonlyField = elementInfo.isReadonly;
              changed = true;
            }
          }
        }
      }
      if (changed) {
        this.props.updateSection(this.props.sectionIndex, {
          ...this.props.section,
          items
        });
      }
    } catch {}
  };

  private getRecommendedElementTypeForItem = (item: ICRUDFormField): string | undefined => {
    try {
      if (this.props.formSource !== EFormSource.WorkflowBo) return undefined;
      const dict:
        | Record<
            string,
            {
              paramName: string;
              paramElement: string;
              isRequired?: boolean;
              isReadonly?: boolean;
            }[]
          >
        | undefined = (window as any)?.kuikaWorkflowParamElements;
      const processId = this.props.metadata?.linkedWorkflowBo?.processId;
      const stepId = this.props.metadata?.linkedWorkflowBo?.stepId;
      if (!dict || !processId || !stepId) return undefined;

      const process = this.props.sd.workFlowList.find((wf) => wf.id === processId);
      const processAny: any = process;
      const step = processAny?.nodes?.steps?.find((s: any) => s.id === stepId);
      const screenName = step?.name;
      if (!screenName) return undefined;
      const entries = dict[screenName] || [];
      if (!entries.length) return undefined;
      const map = new Map<string, { paramElement: string; isRequired?: boolean; isReadonly?: boolean }>(
        entries.map((e) => [
          e.paramName?.toLowerCase(),
          {
            paramElement: e.paramElement,
            isRequired: e.isRequired,
            isReadonly: e.isReadonly
          }
        ])
      );

      if (item.source === EFormFieldSource.WorkflowBusinessObjectParameter && item.linkedWorkflowBoParameterName) {
        const key = item.linkedWorkflowBoParameterName.toLowerCase();
        const elementInfo = map.get(key);
        return elementInfo?.paramElement;
      }
      return undefined;
    } catch {
      return undefined;
    }
  };

  getFormVersion = (): number => {
    return this.props.metadata?.version ?? 2;
  };

  private onKeyUpOnInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      this.onClickEditHeader();
    }
  };

  private onCancelRenamingSection = () => {
    this.setState({
      isRenamingSection: false,
      sectionTitle: this.props.section.title ?? `Section ${this.props.sectionIndex + 1}`
    });

    this.props.setSectionsHeaderEditingInProgressValue(false);
  };

  private getSectionTitle = (): React.ReactNode => {
    if (this.state.isRenamingSection) {
      return (
        <div className="form-section-header-title_container-rename-container">
          <KuikaInput
            size="sm"
            value={this.state.sectionTitle}
            onChange={this.onChangeSectionTitle}
            onKeyUp={this.onKeyUpOnInput}
          />
          <KuikaTooltip content="Cancel">
            <KuikaButton
              type="tertiary"
              color="neutral"
              size="sm"
              isIcon
              aria-label="Cancel renaming the section"
              onClick={this.onCancelRenamingSection}
            >
              <CancelCircleSvg />
            </KuikaButton>
          </KuikaTooltip>
        </div>
      );
    }

    return this.state.sectionTitle;
  };

  private onChangeSectionTitle = (_e: React.ChangeEvent<HTMLInputElement>, value: string | number) => {
    this.setState({ sectionTitle: String(value) });
  };

  private onClickEditHeader = () => {
    if (this.state.isRenamingSection) {
      this.props.updateSection(this.props.sectionIndex, {
        ...this.props.section,
        title: this.state.sectionTitle ?? null
      });
      this.setState({ isRenamingSection: false });
      this.props.setSectionsHeaderEditingInProgressValue(false);
      return;
    }

    this.setState({ isRenamingSection: true });
    this.props.setSectionsHeaderEditingInProgressValue(true);
  };

  private getEditButtonTitle = (): string => {
    if (this.state.isRenamingSection) {
      return "Save";
    }

    return this.props.section.title ? "Edit header" : "Add header";
  };

  /** One column-count option: the little preview block, and its name in the dropdown. */
  private renderLayoutOptionLabel = (preview: React.ReactNode, name: string): React.ReactNode => {
    return (
      <span className="form-section-header-options-layout-options--option-label">
        {preview}
        <span className="form-section-header-options-layout-options--option-name">{name}</span>
      </span>
    );
  };

  private getLayoutOptions = (): KuikaSelectboxOption[] => {
    return [
      {
        value: ECRUDFormLayoutType.SingleColumn,
        searchLabel: "Single Column",
        label: this.renderLayoutOptionLabel(
          <svg xmlns="http://www.w3.org/2000/svg" width="36" height="24" viewBox="0 0 36 24">
            <rect width="36" height="24" fill="var(--dark-2)" rx="2" />
            <rect width="30" height="18" x="3" y="3" fill="var(--dark-4)" rx="1" />
          </svg>,
          "Single Column"
        )
      },
      {
        value: ECRUDFormLayoutType.TwoColumn,
        searchLabel: "2 Columns",
        label: this.renderLayoutOptionLabel(
          <svg xmlns="http://www.w3.org/2000/svg" width="36" height="24" viewBox="0 0 36 24">
            <rect width="36" height="24" fill="var(--dark-2)" rx="2" />
            <rect width="14.25" height="18" x="3" y="3" fill="var(--dark-4)" rx="1" />
            <rect width="14.25" height="18" x="18.75" y="3" fill="var(--dark-4)" rx="1" />
          </svg>,
          "2 Columns"
        )
      },
      {
        value: ECRUDFormLayoutType.FourColumn,
        searchLabel: "4 Columns",
        label: this.renderLayoutOptionLabel(
          <svg xmlns="http://www.w3.org/2000/svg" width="36" height="24" viewBox="0 0 36 24">
            <rect width="36" height="24" fill="var(--dark-2)" rx="2" />
            <rect width="6.375" height="18" x="3" y="3" fill="var(--dark-4)" rx="1" />
            <rect width="6.375" height="18" x="10.875" y="3" fill="var(--dark-4)" rx="1" />
            <rect width="6.375" height="18" x="18.75" y="3" fill="var(--dark-4)" rx="1" />
            <rect width="6.375" height="18" x="26.625" y="3" fill="var(--dark-4)" rx="1" />
          </svg>,
          "4 Columns"
        )
      }
    ];
  };

  private renderLayoutOption = (layout: "desktop" | "tablet" | "mobile") => {
    return (
      <KuikaSelectbox
        size="sm"
        value={this.props.section.responsiveLayoutSizes[layout]}
        className="form-section-header-options-layout-options--option-select"
        overlayClassName="form-section-layout-dropdown"
        // The trigger is a 148px preview well; its options are full names and must not wrap.
        dropdownMatchSelectWidth={false}
        options={this.getLayoutOptions()}
        onChange={(value: string | number | null) => {
          if (value === null) {
            return;
          }

          const section: IFormSection = _.cloneDeep(this.props.section);
          section.responsiveLayoutSizes[layout] = value as ECRUDFormLayoutType;
          this.props.updateSection(this.props.sectionIndex, section);
        }}
      />
    );
  };

  private getItemStyle = (isDragging: boolean, draggableStyle): React.CSSProperties => ({
    // some basic styles to make the items look a bit nicer
    userSelect: "none",

    // change background colour if dragging
    background: isDragging ? "var(--blue-05)" : "var(--dark-0)",

    // styles we need to apply on draggables
    ...draggableStyle
  });

  private onChangeIsRequired = (e: React.ChangeEvent<HTMLInputElement>, formFieldIndex: number) => {
    const items: ICRUDFormField[] = _.cloneDeep(this.props.section.items);
    items[formFieldIndex].isRequired = e.target.checked;

    this.props.updateSection(this.props.sectionIndex, {
      ...this.props.section,
      items
    });
  };

  private onChangeIsReadonly = (e: React.ChangeEvent<HTMLInputElement>, formFieldIndex: number) => {
    const items: ICRUDFormField[] = _.cloneDeep(this.props.section.items);
    items[formFieldIndex].isReadonlyField = e.target.checked;
    items[formFieldIndex].isRequired = false;

    this.props.updateSection(this.props.sectionIndex, {
      ...this.props.section,
      items
    });
  };

  private toUpperCamelCase = (str: string): string => {
    const words = str.match(/[a-zA-Z\u00C0-\u024F\u1E00-\u1EFF]+/gi);
    if (!words) return "";
    return words
      .map(function (word) {
        return word.charAt(0).toUpperCase() + word.substr(1).toLowerCase();
      })
      .join("");
  };

  private convertFieldFieldNameToCorrectFormat = (fieldName: string): string => {
    const str: string = _.deburr(fieldName).replace(/[^a-zA-Z\u00C0-\u024F\u1E00-\u1EFF_]/g, "");
    const splitted: string[] = str.split("");

    if (splitted[0] === "_") {
      splitted.shift();
    }

    if (splitted[0]) {
      splitted[0] = splitted[0].toUpperCase();
    }

    return splitted.join("");
  };

  private convertTitleToFieldName = (title: string): string => {
    return _.deburr(this.toUpperCamelCase(title).replace(/[^a-zA-Z\u00C0-\u024F\u1E00-\u1EFF]/g, ""));
  };

  private onChangeFormFieldTitle = (e: React.ChangeEvent<HTMLInputElement>, formFieldIndex: number) => {
    const items: ICRUDFormField[] = _.cloneDeep(this.props.section.items);
    items[formFieldIndex].name = e.target.value;

    if (
      this.getIsTextFieldNameEnabled() &&
      this.props.idsOfFieldsWithNewColumnActive.includes(items[formFieldIndex].id) &&
      !this.props.idsOfFieldsWhichFieldNamesAreTouched.includes(items[formFieldIndex].id)
    ) {
      items[formFieldIndex].linkedColumnName = this.convertTitleToFieldName(e.target.value);
    }

    this.props.updateSection(this.props.sectionIndex, {
      ...this.props.section,
      items
    });
  };

  private onChangeIsEnabled = (e: React.ChangeEvent<HTMLInputElement>, formFieldIndex: number) => {
    const items: ICRUDFormField[] = _.cloneDeep(this.props.section.items);
    items[formFieldIndex].isEnabled = e.target.checked;

    this.props.updateSection(this.props.sectionIndex, {
      ...this.props.section,
      items
    });
  };

  private onChangeFormFieldType = (value: string | number | null, formFieldIndex: number) => {
    if (!value) return;
    const items: ICRUDFormField[] = _.cloneDeep(this.props.section.items);
    const formField: ICRUDFormField = items[formFieldIndex];

    if (formField.elementType === "" || !this.getCompatibleFieldTypes(formField.typeName).includes(value.toString())) {
      const elementTypes = this.props.getElementTypeOptions(value.toString());
      formField.elementType = elementTypes[0].name ?? "";
    }

    formField.typeName = value.toString();

    if (value.toString() === "Boolean") {
      formField.isRequired = false;
    }

    this.props.updateSection(this.props.sectionIndex, {
      ...this.props.section,
      items
    });
  };

  private onChangeFormFieldElementType = (value: string | number | null, formFieldIndex: number) => {
    if (!value) return;
    const items: ICRUDFormField[] = _.cloneDeep(this.props.section.items);
    items[formFieldIndex].elementType = value.toString();

    // If selected component is checkbox or toggle switch, make isRequired false
    if (this.getIsCheckboxOrToggleSwitch(items[formFieldIndex])) {
      items[formFieldIndex].isRequired = false;
    }

    this.props.updateSection(this.props.sectionIndex, {
      ...this.props.section,
      items
    });
  };

  private getIsAddItemDisabled = (): boolean => {
    const lastItem: ICRUDFormField | undefined = this.props.section.items.at(-1);
    if (!lastItem) return false;

    return _.isEmpty(lastItem.name) && _.isEmpty(lastItem.elementType) && !lastItem.isRequired;
  };

  private getKuikaTypes = (): string[] => {
    return [
      "Boolean",
      "DatetimeOffset",
      "Decimal",
      "Double",
      // "Guid",
      "Image",
      "Int32",
      "Int16",
      "Int64",
      "Signature",
      "String"
      // "AutoIncrement",
      // "Bytea"
    ];
  };

  private getTypeIcon = (type: string) => {
    switch (type?.toLowerCase()) {
      case "unknown":
        return <StringSvg />;
      case "boolean":
        return <BooleanSvg />;
      case "bytea":
        return <DoubleSvg />;
      case "byte":
        return <DoubleSvg />;
      case "date":
        return <DateSvg />;
      case "datetime":
        return <DateTimeSvg />;
      case "location":
        return <LocationSvg />;
      case "filecontent":
        return <FileContentSvg />;
      case "datetimeoffset":
        return <DateTimeOffsetSvg />;
      case "decimal":
        return <DecimalSvg />;
      case "double":
        return <DoubleSvg />;
      case "float":
        return <DoubleSvg />;
      case "varbinary":
        return <DoubleSvg />;
      case "guid":
        return <GuidSvg />;
      case "int16":
        return <Int16Svg />;
      case "int32":
        return <Int32Svg />;
      case "int64":
        return <Int64Svg />;
      case "single":
        return <DoubleSvg />;
      case "string":
        return <StringSvg />;
      case "image":
        return <ImageSvg />;
      case "int":
        return <Int32Svg />;
      case "bigint":
        return <Int64Svg />;
      case "smallint":
        return <Int16Svg />;
      case "nvarchar":
        return <StringSvg />;
      case "uniqueidentifier":
        return <GuidSvg />;
      case "bit":
        return <BooleanSvg />;
      default:
        return <StringSvg />;
    }
  };

  private buildElementTypeOption = (value: string, label: string, isRecommended: boolean): KuikaSelectboxOption => {
    return {
      value,
      searchLabel: label,
      label: (
        <span className="form-section-option form-section-option--spread">
          <span className="form-section-option__main">
            <KuikaComponentIcon name={value} width={16} height={16} className="form-section-option__component-icon" />
            <span>{label}</span>
          </span>
          {isRecommended && (
            <KuikaBadge
              content={
                <span className="form-section-option__ai">
                  <KuikaSvg type="datasource-icons" name="sparkles" width={10} height={10} />
                  <span>AI</span>
                </span>
              }
              color="purple"
            />
          )}
        </span>
      )
    };
  };

  private getCompatibleFieldTypes = (elementType: string): string[] => {
    switch (elementType) {
      case "Date":
      case "Datetime":
      case "DatetimeOffset":
        return ["Date", "Datetime", "DatetimeOffset"].filter((type) => type !== elementType);

      case "Decimal":
      case "Double":
      case "Int32":
      case "Int16":
      case "Int64":
        return ["Decimal", "Double", "Int32", "Int16", "Int64"].filter((type) => type !== elementType);

      case "String":
      case "Guid":
        return ["String", "Guid"].filter((type) => type !== elementType);

      default:
        return [];
    }
  };

  private getElementTypeSelectOptions = (formFieldIndex: number): KuikaSelectboxOption[] => {
    const item: ICRUDFormField = this.props.section.items[formFieldIndex];
    const recommended = this.getRecommendedElementTypeForItem(item);

    return this.props
      .getElementTypeOptions(item.typeName)
      .filter((option) => {
        if (this.props.sd.project.projectType === EProjectType.Mobile) {
          return !option.isWebOnly;
        }

        return true;
      })
      .map((option: IElementTypeOption) =>
        this.buildElementTypeOption(option.name, option.label, Boolean(recommended && recommended === option.name))
      );
  };

  getIsTextFieldNameEnabled = (): boolean => {
    if (this.getIsAlsoCreateTableEnabled()) {
      return true;
    }

    if (this.props.formSource === EFormSource.DataTable) {
      return true;
    }

    if (this.props.formSource === EFormSource.WorkflowBo) {
      return true;
    }

    return false;
  };

  getIsBoOptionDisabled = (formField: ICRUDFormField, parameter: IWFParameter): boolean => {
    // return this.props.section.items.some(
    //   (item: ICRUDFormField) => item.linkedWorkflowBoParameterName === parameter.name && item.id !== formField.id
    // );

    return false;
  };

  getIsFieldOptionDisabled = (formField: ICRUDFormField, column: IDatabaseColumnModel): boolean => {
    if (this.getFormVersion() === 1) {
      return this.props.section.items.some((item: ICRUDFormField) => {
        return item.linkedColumnId === column.id && item.id !== formField.id;
      });
    }

    return this.props.section.items.some((item: ICRUDFormField) => {
      return item.linkedColumnName === column.name && item.id !== formField.id;
    });
  };

  getIsDisplayStaticFieldType = (formField: ICRUDFormField): boolean => {
    if (
      this.props.formSource === EFormSource.DataTable &&
      this.getIsFieldLinkedToColumn(formField) &&
      !this.props.idsOfFieldsWithNewColumnActive.includes(formField.id)
    ) {
      return true;
    }

    if (
      this.props.formSource === EFormSource.WorkflowBo &&
      formField.source === EFormFieldSource.WorkflowBusinessObjectParameter
    ) {
      return true;
    }

    return false;
  };

  getIsDisplayStaticFieldName = (formField: ICRUDFormField): boolean => {
    if (
      this.props.formSource === EFormSource.WorkflowBo &&
      formField.source === EFormFieldSource.WorkflowBusinessObjectParameter
    ) {
      return true;
    }

    if (this.props.formSource === EFormSource.WorkflowBo && formField.source === EFormFieldSource.Custom) {
      return true;
    }

    return false;
  };

  getIsFieldLinkedToColumn = (formField: ICRUDFormField): boolean => {
    if (formField.source !== EFormFieldSource.TableColumn) {
      return false;
    }

    if (this.getFormVersion() === 1) {
      return !_.isEmpty(formField.linkedColumnId);
    }

    return !_.isEmpty(formField.linkedColumnName);
  };

  private getFieldNameValue = (formField: ICRUDFormField) => {
    if (formField.source === EFormFieldSource.Custom) {
      return "not-bound";
    }

    if (formField.source === EFormFieldSource.TableColumn) {
      return formField.linkedColumnName;
    }

    if (formField.source === EFormFieldSource.WorkflowBusinessObjectParameter) {
      return formField.linkedWorkflowBoParameterName;
    }

    return formField.linkedColumnName;
  };

  private onChangeFieldNameViaDropdown = (value: string | number | null, formFieldIndex: number) => {
    if (!value) return;

    if (value === "new-column") {
      const items: ICRUDFormField[] = _.cloneDeep(this.props.section.items);
      items[formFieldIndex].linkedColumnName = this.convertTitleToFieldName(items[formFieldIndex].name);
      items[formFieldIndex].source = EFormFieldSource.TableColumn;
      this.props.setFieldsNewColumnActiveValue(this.props.section.items[formFieldIndex].id, true);
      this.props.updateSection(this.props.sectionIndex, {
        ...this.props.section,
        items
      });
      return;
    }

    if (value === "not-bound") {
      const items: ICRUDFormField[] = _.cloneDeep(this.props.section.items);
      delete items[formFieldIndex].linkedColumnName;
      items[formFieldIndex].source = EFormFieldSource.Custom;

      this.props.updateSection(this.props.sectionIndex, {
        ...this.props.section,
        items
      });
      return;
    }

    if (this.props.formSource === EFormSource.DataTable) {
      // Column selection case
      const items: ICRUDFormField[] = _.cloneDeep(this.props.section.items);
      const table: IDatabaseTableModel | undefined = this.props.tables?.find(
        (table: IDatabaseTableModel) => `${table.schema}.${table.name}` === this.props.metadata?.linkedTable?.tableName
      );
      const column: IDatabaseColumnModel | undefined = table?.columns?.find(
        (column: IDatabaseColumnModel) => column.name.toLowerCase() === value?.toString().toLowerCase()
      );

      if (column) {
        items[formFieldIndex].source = EFormFieldSource.TableColumn;
        items[formFieldIndex].linkedColumnName = column.name;
        items[formFieldIndex].typeName = column.kuikaDataType;
        items[formFieldIndex].elementType = this.props.getElementTypeOptions(column.kuikaDataType)[0].name;
      }

      this.props.updateSection(this.props.sectionIndex, {
        ...this.props.section,
        items
      });
    }

    if (this.props.formSource === EFormSource.WorkflowBo) {
      const items: ICRUDFormField[] = _.cloneDeep(this.props.section.items);
      const parameter: IWFParameter | undefined = this.getCurrentWorkflowBoFields().find(
        (parameter: IWFParameter) => parameter.name === value?.toString()
      );

      if (parameter) {
        items[formFieldIndex].source = EFormFieldSource.WorkflowBusinessObjectParameter;
        items[formFieldIndex].linkedWorkflowBoParameterId = parameter.id;
        items[formFieldIndex].linkedWorkflowBoParameterName = parameter.name;
        items[formFieldIndex].typeName = parameter.dataType;
        items[formFieldIndex].elementType = this.props.getElementTypeOptions(parameter.dataType)[0].name;
      }
    }
  };

  private onChangeFieldName = (value: string | number | null, formFieldIndex: number) => {
    const items: ICRUDFormField[] = _.cloneDeep(this.props.section.items);

    items[formFieldIndex].linkedColumnName = this.convertFieldFieldNameToCorrectFormat(value?.toString() ?? "");
    items[formFieldIndex].source = EFormFieldSource.TableColumn;

    if (
      this.getIsTextFieldNameEnabled() &&
      this.props.idsOfFieldsWithNewColumnActive.includes(items[formFieldIndex].id) &&
      !this.props.idsOfFieldsWhichFieldNamesAreTouched.includes(items[formFieldIndex].id)
    ) {
      this.props.setFieldsTouchedValue(this.props.section.items[formFieldIndex].id, true);
    }

    this.props.updateSection(this.props.sectionIndex, {
      ...this.props.section,
      items
    });
  };

  private onClickChooseExistingColumn = (formFieldIndex: number) => {
    const formField: ICRUDFormField = this.props.section.items[formFieldIndex];
    this.props.setFieldsTouchedValue(formField.id, false);
    this.onChangeFieldNameViaDropdown("not-bound", formFieldIndex);
  };

  private getCurrentTableFields = (): IDatabaseColumnModel[] => {
    return (
      this.props.tables
        ?.find((table) => `${table.schema}.${table.name}` === this.props.metadata?.linkedTable?.tableName)
        ?.columns?.filter(
          (column) =>
            column.name !== "Id" && column.kuikaDataType !== "AutoIncrement" && column.kuikaDataType !== "Bytea"
        ) || []
    );
  };

  private getCurrentWorkflowBoFields = () => {
    const processId: string | undefined = this.props.metadata?.linkedWorkflowBo?.processId;

    if (!processId) {
      return [];
    }

    const process: IWorkFlow | undefined = this.props.sd.workFlowList.find(
      (process: IWorkFlow) => process.id === processId
    );

    if (!process) {
      return [];
    }

    return process.parameters?.filter((parameter: IWFParameter) => parameter.isBusinessObject) || [];
  };

  getIsReadOnlyFeatureEnabled = (): boolean => {
    return this.props.formSource === EFormSource.WorkflowBo;
  };

  private getIsCheckboxOrToggleSwitch = (item: ICRUDFormField): boolean => {
    return (
      item.elementType === CRUDInputElementConstants.Checkbox ||
      item.elementType === CRUDInputElementConstants.ToggleSwitch
    );
  };

  renderStaticTableCellContent = (text: string, icon?: React.ReactNode) => {
    return (
      <span className="form-section-option">
        {icon}
        <span>{text}</span>
      </span>
    );
  };

  /** The glyph for a field bound to nothing — a broken link. */
  private renderUnlinkIcon = (): React.ReactNode => {
    return (
      <span className="form-section-option__icon">
        <KuikaSvg name="unlink" width={16} height={16} color="var(--dark-7)" />
      </span>
    );
  };

  /** What a field can be bound to: nothing, a new column, or one that already exists. */
  private getFieldNameSelectOptions = (item: ICRUDFormField): KuikaSelectboxOption[] => {
    const options: KuikaSelectboxOption[] = [
      {
        value: "not-bound",
        searchLabel: "Not Bound",
        label: (
          <span className="form-section-option">
            {this.renderUnlinkIcon()}
            <span>Not Bound</span>
          </span>
        )
      }
    ];

    if (this.props.formSource === EFormSource.DataTable) {
      options.push({
        value: "new-column",
        searchLabel: "New Column",
        label: (
          <span className="form-section-option">
            <span className="form-section-option__icon">
              <PlusIconSvg style={{ width: 16, height: 16 }} />
            </span>
            <span>New Column</span>
          </span>
        )
      });

      this.getCurrentTableFields().forEach((column: IDatabaseColumnModel) => {
        options.push({
          value: column.name,
          searchLabel: column.name,
          disabled: this.getIsFieldOptionDisabled(item, column),
          label: (
            <span className="form-section-option">
              {this.getTypeIcon(item.typeName)}
              <span>{column.name}</span>
            </span>
          )
        });
      });
    }

    if (this.props.formSource === EFormSource.WorkflowBo) {
      this.getCurrentWorkflowBoFields().forEach((field: IWFParameter) => {
        options.push({
          value: field.name,
          searchLabel: field.name,
          disabled: this.getIsBoOptionDisabled(item, field),
          label: (
            <span className="form-section-option">
              {this.getTypeIcon(field.dataType)}
              <span>{field.name}</span>
            </span>
          )
        });
      });
    }

    return options;
  };

  /**
   * The Kuika primitives, then the project's own tables — picking a table makes the generated
   * column a foreign key to it. Both kinds carry the test ids the E2E suite addresses them by.
   */
  private getFieldTypeSelectOptions = (): KuikaSelectboxOption[] => {
    const options: KuikaSelectboxOption[] = this.getKuikaTypes().map((type: string): KuikaSelectboxOption => ({
      value: type,
      searchLabel: type,
      label: (
        <span className="form-section-option" data-testid={`${type}Type`}>
          {this.getTypeIcon(type)}
          <span>{type}</span>
        </span>
      )
    }));

    if (this.props.formSource !== EFormSource.DataTable && this.props.formSource !== EFormSource.Custom) {
      return options;
    }

    this.props.tables
      .filter(
        (table: IDatabaseTableModel) => `${table.schema}.${table.name}` !== this.props.metadata?.linkedTable?.tableName
      )
      .forEach((table: IDatabaseTableModel) => {
        options.push({
          value: `${table.schema}.${table.name}`,
          searchLabel: `${table.name} ${table.schema}`,
          label: (
            <span className="form-section-option" data-testid={`${table.name}TableType`}>
              <span className="form-section-option__icon">
                <TableSvg style={{ width: 15, height: 15 }} />
              </span>
              <span>{table.name}</span>
              <KuikaBadge content={table.schema} color="grey" />
            </span>
          )
        });
      });

    return options;
  };

  render() {
    return (
      <div className="form-section">
        <div className="form-section-header">
          <div className="form-section-header-title_container">
            <div className="form-section-header-title_container--title">{this.getSectionTitle()}</div>
            <KuikaButton
              type="tertiary"
              size="sm"
              onClick={this.onClickEditHeader}
              data-testid={`createFormModalSectionHeaderButton-${this.props.sectionIndex}`}
            >
              {this.getEditButtonTitle()}
            </KuikaButton>
          </div>
          <div className="form-section-header-options">
            <div className="form-section-header-options-section-layout-title">Section layout</div>
            <div className="form-section-header-options-layout-options">
              <div className="form-section-header-options-layout-options--option">
                <DesktopSvg />
                {this.renderLayoutOption("desktop")}
              </div>
              <div className="form-section-header-options-layout-options--option">
                <TabletSvg />
                {this.renderLayoutOption("tablet")}
              </div>
              <div className="form-section-header-options-layout-options--option">
                <MobileSvg />
                {this.renderLayoutOption("mobile")}
              </div>
            </div>
          </div>
        </div>

        <Droppable droppableId={`droppable-section-${this.props.sectionIndex}`}>
          {(provided: DroppableProvided) => (
            <table className="form-section-table" ref={provided.innerRef}>
              <thead>
                <tr>
                  <th></th>
                  <th>Label</th>
                  {this.getIsTextFieldNameEnabled() && <th>Field Name</th>}
                  <th>Field Type</th>
                  <th>Element Type</th>
                  <th title="Required">Req.</th>
                  {this.getIsReadOnlyFeatureEnabled() && <th title="Readonly">R/O</th>}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {this.props.section.items.map((item: ICRUDFormField, index: number) => (
                  <Draggable key={item.id} draggableId={item.id} index={index}>
                    {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                      <tr
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={this.getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                      >
                        <td style={{ width: "32px" }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              maxWidth: "fit-content",
                              gap: 5,
                              paddingLeft: 5,
                              paddingRight: 5
                            }}
                          >
                            <DragHandleSvg
                              className="form-section-table--drag-handle"
                              style={{
                                opacity: snapshot.isDragging ? 1 : undefined
                              }}
                            />
                            <KuikaCheckbox
                              checked={item.isEnabled}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.onChangeIsEnabled(e, index)}
                              dataTestId={`createFormModalCustomTableEnabledCheckbox-${item.id}`}
                            />
                          </div>
                        </td>
                        <td style={{ width: "232px" }}>
                          <KuikaInput
                            size="sm"
                            borderless
                            data-testid="createFormModalCustomTableLabelInput"
                            value={item.name}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.onChangeFormFieldTitle(e, index)}
                          />
                        </td>
                        {this.getIsTextFieldNameEnabled() && (
                          <td style={{ width: "232px" }}>
                            {this.getIsDisplayStaticFieldName(item) ? (
                              <div
                                style={{
                                  display: "flex",
                                  gap: 8,
                                  alignItems: "center",
                                  marginLeft: 12
                                }}
                              >
                                {this.getFieldNameValue(item) === "not-bound"
                                  ? this.renderStaticTableCellContent(
                                      "Not Bound",
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          width: 20,
                                          height: 20
                                        }}
                                      >
                                        <svg
                                          width="16"
                                          height="16"
                                          viewBox="0 0 15 15"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <path
                                            d="M13.3536 2.35355C13.5488 2.15829 13.5488 1.84171 13.3536 1.64645C13.1583 1.45118 12.8417 1.45118 12.6464 1.64645L1.64645 12.6464C1.45118 12.8417 1.45118 13.1583 1.64645 13.3536C1.84171 13.5488 2.15829 13.5488 2.35355 13.3536L13.3536 2.35355ZM2.03735 8.46678C2.17398 9.12619 2.66918 9.67103 3.33886 9.89338L2.57833 10.6539C1.80843 10.2534 1.23784 9.53693 1.05815 8.66967C0.999538 8.38681 0.999604 8.06004 0.999703 7.56313L0.999711 7.50001L0.999703 7.43689C0.999604 6.93998 0.999538 6.61321 1.05815 6.33035C1.29846 5.17053 2.2379 4.28039 3.4182 4.055C3.70687 3.99988 4.04134 3.99993 4.56402 4.00001L4.62471 4.00001H5.49971C5.77585 4.00001 5.99971 4.22387 5.99971 4.50001C5.99971 4.77615 5.77585 5.00001 5.49971 5.00001H4.62471C4.02084 5.00001 3.78907 5.00225 3.60577 5.03725C2.80262 5.19062 2.19157 5.78895 2.03735 6.53324C2.00233 6.70225 1.99971 6.91752 1.99971 7.50001C1.99971 8.08251 2.00233 8.29778 2.03735 8.46678ZM12.9621 6.53324C12.8255 5.87397 12.3304 5.32922 11.661 5.10679L12.4215 4.34631C13.1912 4.74686 13.7616 5.46323 13.9413 6.33035C13.9999 6.61321 13.9998 6.93998 13.9997 7.43688L13.9997 7.50001L13.9997 7.56314C13.9998 8.06005 13.9999 8.38681 13.9413 8.66967C13.701 9.8295 12.7615 10.7196 11.5812 10.945C11.2925 11.0001 10.9581 11.0001 10.4354 11L10.3747 11H9.49971C9.22357 11 8.99971 10.7762 8.99971 10.5C8.99971 10.2239 9.22357 10 9.49971 10H10.3747C10.9786 10 11.2104 9.99777 11.3937 9.96277C12.1968 9.8094 12.8079 9.21108 12.9621 8.46678C12.9971 8.29778 12.9997 8.08251 12.9997 7.50001C12.9997 6.91752 12.9971 6.70225 12.9621 6.53324Z"
                                            fill="currentColor"
                                            fill-rule="evenodd"
                                            clip-rule="evenodd"
                                          ></path>
                                        </svg>
                                      </div>
                                    )
                                  : this.renderStaticTableCellContent(
                                      this.getFieldNameValue(item) ?? "",
                                      this.getTypeIcon(item.typeName)
                                    )}
                              </div>
                            ) : this.props.idsOfFieldsWithNewColumnActive.includes(item.id) ? (
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "row"
                                }}
                              >
                                <KuikaInput
                                  size="sm"
                                  borderless
                                  data-testid="createFormModalCustomTableFieldNameInput"
                                  value={item.linkedColumnName}
                                  onChange={(_e: React.ChangeEvent<HTMLInputElement>, value: string | number) =>
                                    this.onChangeFieldName(String(value), index)
                                  }
                                />

                                {!this.getIsAlsoCreateTableEnabled() && (
                                  <KuikaTooltip content="Click to select an existing column instead">
                                    <KuikaButton
                                      size="sm"
                                      isIcon
                                      aria-label="Select an existing column instead"
                                      className="form-section-table--pick-column"
                                      onClick={() => this.onClickChooseExistingColumn(index)}
                                    >
                                      <svg
                                        width="15"
                                        height="15"
                                        viewBox="0 0 15 15"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path
                                          d="M1.5 5.25C1.91421 5.25 2.25 4.91421 2.25 4.5C2.25 4.08579 1.91421 3.75 1.5 3.75C1.08579 3.75 0.75 4.08579 0.75 4.5C0.75 4.91421 1.08579 5.25 1.5 5.25ZM4 4.5C4 4.22386 4.22386 4 4.5 4H13.5C13.7761 4 14 4.22386 14 4.5C14 4.77614 13.7761 5 13.5 5H4.5C4.22386 5 4 4.77614 4 4.5ZM4.5 7C4.22386 7 4 7.22386 4 7.5C4 7.77614 4.22386 8 4.5 8H13.5C13.7761 8 14 7.77614 14 7.5C14 7.22386 13.7761 7 13.5 7H4.5ZM4.5 10C4.22386 10 4 10.2239 4 10.5C4 10.7761 4.22386 11 4.5 11H13.5C13.7761 11 14 10.7761 14 10.5C14 10.2239 13.7761 10 13.5 10H4.5ZM2.25 7.5C2.25 7.91421 1.91421 8.25 1.5 8.25C1.08579 8.25 0.75 7.91421 0.75 7.5C0.75 7.08579 1.08579 6.75 1.5 6.75C1.91421 6.75 2.25 7.08579 2.25 7.5ZM1.5 11.25C1.91421 11.25 2.25 10.9142 2.25 10.5C2.25 10.0858 1.91421 9.75 1.5 9.75C1.08579 9.75 0.75 10.0858 0.75 10.5C0.75 10.9142 1.08579 11.25 1.5 11.25Z"
                                          fill="currentColor"
                                          fill-rule="evenodd"
                                          clip-rule="evenodd"
                                        ></path>
                                      </svg>
                                    </KuikaButton>
                                  </KuikaTooltip>
                                )}
                              </div>
                            ) : (
                              <KuikaSelectbox
                                size="sm"
                                borderless
                                searchable
                                dropdownMatchSelectWidth={false}
                                value={this.getFieldNameValue(item)}
                                options={this.getFieldNameSelectOptions(item)}
                                overlayClassName="form-section-option-dropdown"
                                onChange={(value: string | number | null) =>
                                  this.onChangeFieldNameViaDropdown(value, index)
                                }
                              />
                            )}
                          </td>
                        )}
                        <td style={{ width: "269px" }}>
                          {this.getIsDisplayStaticFieldType(item) ? (
                            <div
                              data-testid="createFormModalCustomTableFieldTypeStatic"
                              style={{
                                display: "flex",
                                gap: 8,
                                alignItems: "center",
                                marginLeft: 12
                              }}
                            >
                              {this.getTypeIcon(item.typeName)}
                              <span>{item.typeName}</span>
                            </div>
                          ) : (
                            <KuikaSelectbox
                              size="sm"
                              borderless
                              searchable
                              data-testid="createFormModalCustomTableFieldTypeDropdown"
                              dropdownMatchSelectWidth={false}
                              value={item.typeName}
                              options={this.getFieldTypeSelectOptions()}
                              overlayClassName="form-section-option-dropdown"
                              onChange={(value: string | number | null) =>
                                value !== null && this.onChangeFormFieldType(String(value), index)
                              }
                            />
                          )}
                        </td>
                        <td style={{ width: "315px" }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8
                            }}
                          >
                            <KuikaSelectbox
                              size="sm"
                              borderless
                              searchable
                              data-testid="createFormModalCustomTableElementTypeDropdown"
                              dropdownMatchSelectWidth={false}
                              value={item.elementType}
                              options={this.getElementTypeSelectOptions(index)}
                              overlayClassName="form-section-option-dropdown"
                              onChange={(value: string | number | null) =>
                                value !== null && this.onChangeFormFieldElementType(String(value), index)
                              }
                            />
                            {(item.elementType === CRUDInputElementConstants.Select ||
                              item.elementType === CRUDInputElementConstants.MultiSelect) && (
                              <KuikaTooltip content="Manual setup required for Selectbox/MultiSelect components">
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="var(--orange-6)"
                                  style={{ flexShrink: 0 }}
                                >
                                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                                </svg>
                              </KuikaTooltip>
                            )}
                          </div>
                        </td>
                        <td style={{ width: "44px" }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              height: "100%",
                              width: "100%"
                            }}
                          >
                            <KuikaCheckbox
                              dataTestId="createFormModalCustomTableRequiredCheckbox"
                              checked={item.isRequired}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.onChangeIsRequired(e, index)}
                              disabled={
                                (this.getIsReadOnlyFeatureEnabled() && item.isReadonlyField) ||
                                this.getIsCheckboxOrToggleSwitch(item)
                              }
                            />
                          </div>
                        </td>
                        {this.getIsReadOnlyFeatureEnabled() && (
                          <td style={{ width: "44px" }}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                height: "100%",
                                width: "100%"
                              }}
                            >
                              <KuikaCheckbox
                                checked={item.isReadonlyField}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.onChangeIsReadonly(e, index)}
                                dataTestId={`createFormModalCustomTableReadonlyCheckbox-${item.id}`}
                              />
                            </div>
                          </td>
                        )}
                        <td style={{ width: "20px" }}>
                          <KuikaDropdownMenu
                            placement="bottomRight"
                            options={[
                              {
                                key: `${item.id}-delete-item`,
                                label: "Delete",
                                danger: true,
                                onClick: () => this.props.onClickDeleteItem(this.props.sectionIndex, index)
                              }
                            ]}
                          >
                            <KuikaButton
                              type="tertiary"
                              color="neutral"
                              size="sm"
                              isIcon
                              aria-label="Field actions"
                              data-testid={`createFormModalCustomTableFieldActions-${item.id}`}
                            >
                              <DetailMenuSvg />
                            </KuikaButton>
                          </KuikaDropdownMenu>
                        </td>
                      </tr>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </tbody>
            </table>
          )}
        </Droppable>
        <div className="form-section-add-item">
          <KuikaButton
            type="tertiary"
            size="sm"
            onClick={() => this.props.onClickAddItem(this.props.sectionIndex)}
            disabled={this.getIsAddItemDisabled()}
            data-testid={`createFormModalAddItem-${this.props.sectionIndex}`}
          >
            Add item
          </KuikaButton>
        </div>
      </div>
    );
  }
}

const formSection = withSD<IFormSectionProps>(FormSection);
export { formSection as FormSection };
